// Shared Bible-related constants.

// Spanish reading translations. When one of these is the active reading
// translation, the interlinear overlay uses the Spanish RV1909+Strong's tagged
// text (and the Spanish concordance) instead of the English KJV+Strong's set.
export const SPANISH_ABBRS = [
  'RVR1960', 'RVR1995', 'RVR1909', 'BSO', 'LBLA', 'RVA',
  'NVI', 'NTV', 'TLA', 'DHH', 'PDT', 'PDPT', 'BLP', 'CST', 'JBS',
  'ONBV', 'RVG', 'VBL', 'ESS', 'BLM', 'RV1865', 'TA', 'BLL', 'JUN', 'RV1602', 'RV1569',
];

// Returns the Strong's-tagged translation to overlay for a given reading
// translation (Spanish → RV1909+Strong's, otherwise KJV+Strong's).
export const strongsTranslationFor = (translation) =>
  SPANISH_ABBRS.includes(translation) ? "RV1909+Strong's" : "KJV+Strong's";

// Translations that can be fully downloaded for offline reading. Mirrors the
// backend `OFFLINE_AVAILABLE_TRANSLATIONS` (bible_routes.py) — keep in sync.
export const OFFLINE_AVAILABLE_ABBRS = [
  'KJV', 'BSB', 'NET', 'FBV', 'WEB', 'ASV', 'YLT', 'GNV', 'DRA', 'LSV',
  'RVR1909', 'RVG', 'ONBV', 'ESS', 'PDPT', 'BLM', 'VBL', 'RV1865', 'TA', 'BLL', 'JUN', 'RV1602', 'RV1569',
];
export const isOfflineAvailable = (abbr) => OFFLINE_AVAILABLE_ABBRS.includes(abbr);

// Canonical book groupings.
export const OT_BOOKS = [
  'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy', 'Joshua', 'Judges',
  'Ruth', '1 Samuel', '2 Samuel', '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles',
  'Ezra', 'Nehemiah', 'Esther', 'Job', 'Psalms', 'Proverbs', 'Ecclesiastes',
  'Song of Solomon', 'Isaiah', 'Jeremiah', 'Lamentations', 'Ezekiel', 'Daniel',
  'Hosea', 'Joel', 'Amos', 'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk',
  'Zephaniah', 'Haggai', 'Zechariah', 'Malachi',
];
export const NT_BOOKS = [
  'Matthew', 'Mark', 'Luke', 'John', 'Acts', 'Romans', '1 Corinthians', '2 Corinthians',
  'Galatians', 'Ephesians', 'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians',
  '1 Timothy', '2 Timothy', 'Titus', 'Philemon', 'Hebrews', 'James', '1 Peter', '2 Peter',
  '1 John', '2 John', '3 John', 'Jude', 'Revelation',
];
const OT_SET = new Set(OT_BOOKS);
const NT_SET = new Set(NT_BOOKS);

// Full canonical order (OT then NT) — the single source of truth for book lists.
export const BIBLE_BOOKS = [...OT_BOOKS, ...NT_BOOKS];

// Deuterocanonical books — only present in Catholic Bibles (Torres Amat).
export const DEUTERO_CHAPTERS = {
  'Tobit': 14, 'Judith': 16, 'Wisdom': 19, 'Sirach': 51, 'Baruch': 6, '1 Maccabees': 16, '2 Maccabees': 15,
};
export const CATHOLIC_TRANSLATIONS = ['TA', 'RV1569', 'RV1602', 'JUN'];
const CATHOLIC_OT = (() => {
  const ot = [...OT_BOOKS];
  const after = (b, ...add) => ot.splice(ot.indexOf(b) + 1, 0, ...add);
  after('Nehemiah', 'Tobit', 'Judith');
  after('Song of Solomon', 'Wisdom', 'Sirach');
  after('Lamentations', 'Baruch');
  after('Malachi', '1 Maccabees', '2 Maccabees');
  return ot;
})();
export const ALL_BOOKS = [...BIBLE_BOOKS, ...Object.keys(DEUTERO_CHAPTERS)];
export const booksFor = (translation) =>
  (CATHOLIC_TRANSLATIONS.includes(translation) ? [...CATHOLIC_OT, ...NT_BOOKS] : BIBLE_BOOKS);
export const otBooksFor = (translation) => booksFor(translation).filter((b) => !NT_SET.has(b));

// Single-testament translations (abbr -> 'OT' | 'NT'). A translation may also
// map to an explicit array of available books (partial coverage, e.g. Tyndale
// = NT + Pentateuch + Jonah). Everything else is treated as full.
export const TRANSLATION_COVERAGE = {
  JPS: 'OT', LEE: 'OT', WEY: 'NT', WESLEY: 'NT',
  TYN: [
    'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy', 'Jonah',
    ...NT_BOOKS,
  ],
};
// Whether a book is available in a given translation.
export const isBookAvailable = (translation, book) => {
  if (DEUTERO_CHAPTERS[book]) return CATHOLIC_TRANSLATIONS.includes(translation);
  const cov = TRANSLATION_COVERAGE[translation];
  if (!cov) return true;
  if (Array.isArray(cov)) return cov.includes(book);
  return cov === 'OT' ? OT_SET.has(book) : NT_SET.has(book);
};

// Canonical (Protestant/KJV) chapter counts per book. Used to bound the
// chapter picker so each book only offers its real chapters (not always 1-150).
export const BOOK_CHAPTER_COUNTS = {
  'Genesis': 50, 'Exodus': 40, 'Leviticus': 27, 'Numbers': 36, 'Deuteronomy': 34,
  'Joshua': 24, 'Judges': 21, 'Ruth': 4, '1 Samuel': 31, '2 Samuel': 24,
  '1 Kings': 22, '2 Kings': 25, '1 Chronicles': 29, '2 Chronicles': 36, 'Ezra': 10,
  'Nehemiah': 13, 'Esther': 10, 'Job': 42, 'Psalms': 150, 'Proverbs': 31,
  'Ecclesiastes': 12, 'Song of Solomon': 8, 'Isaiah': 66, 'Jeremiah': 52,
  'Lamentations': 5, 'Ezekiel': 48, 'Daniel': 12, 'Hosea': 14, 'Joel': 3, 'Amos': 9,
  'Obadiah': 1, 'Jonah': 4, 'Micah': 7, 'Nahum': 3, 'Habakkuk': 3, 'Zephaniah': 3,
  'Haggai': 2, 'Zechariah': 14, 'Malachi': 4,
  'Matthew': 28, 'Mark': 16, 'Luke': 24, 'John': 21, 'Acts': 28, 'Romans': 16,
  '1 Corinthians': 16, '2 Corinthians': 13, 'Galatians': 6, 'Ephesians': 6,
  'Philippians': 4, 'Colossians': 4, '1 Thessalonians': 5, '2 Thessalonians': 3,
  '1 Timothy': 6, '2 Timothy': 4, 'Titus': 3, 'Philemon': 1, 'Hebrews': 13, 'James': 5,
  '1 Peter': 5, '2 Peter': 3, '1 John': 5, '2 John': 1, '3 John': 1, 'Jude': 1,
  'Revelation': 22,
};
// Number of chapters for a book (defaults to 150 if unknown).
export const chapterCountFor = (book) => BOOK_CHAPTER_COUNTS[book] || DEUTERO_CHAPTERS[book] || 150;

// Canonical (KJV) verse counts per chapter, indexed [book][chapter-1].
// Used to bound the verse picker so each chapter only offers its real verses
// (not always 1-176). Sourced from the seeded KJV corpus.
export const BOOK_VERSE_COUNTS = {
  'Genesis': [31,25,24,26,32,22,24,22,29,32,32,20,18,24,21,16,27,33,38,18,34,24,20,67,34,35,46,22,35,43,55,32,20,31,29,43,36,30,23,23,57,38,34,34,28,34,31,22,33,26],
  'Exodus': [22,25,22,31,23,30,25,32,35,29,10,51,22,31,27,36,16,27,25,26,36,31,33,18,40,37,21,43,46,38,18,35,23,35,35,38,29,31,43,38],
  'Leviticus': [17,16,17,35,19,30,38,36,24,20,47,8,59,57,33,34,16,30,37,27,24,33,44,23,55,46,34],
  'Numbers': [54,34,51,49,31,27,89,26,23,36,35,16,33,45,41,50,13,32,22,29,35,41,30,25,18,65,23,31,40,16,54,42,56,29,34,13],
  'Deuteronomy': [46,37,29,49,33,25,26,20,29,22,32,32,18,29,23,22,20,22,21,20,23,30,25,22,19,19,26,68,29,20,30,52,29,12],
  'Joshua': [18,24,17,24,15,27,26,35,27,43,23,24,33,15,63,10,18,28,51,9,45,34,16,33],
  'Judges': [36,23,31,24,31,40,25,35,57,18,40,15,25,20,20,31,13,31,30,48,25],
  'Ruth': [22,23,18,22],
  '1 Samuel': [28,36,21,22,12,21,17,22,27,27,15,25,23,52,35,23,58,30,24,42,15,23,29,22,44,25,12,25,11,31,13],
  '2 Samuel': [27,32,39,12,25,23,29,18,13,19,27,31,39,33,37,23,29,33,43,26,22,51,39,25],
  '1 Kings': [53,46,28,34,18,38,51,66,28,29,43,33,34,31,34,34,24,46,21,43,29,53],
  '2 Kings': [18,25,27,44,27,33,20,29,37,36,21,21,25,29,38,20,41,37,37,21,26,20,37,20,30],
  '1 Chronicles': [54,55,24,43,26,81,40,40,44,14,47,40,14,17,29,43,27,17,19,8,30,19,32,31,31,32,34,21,30],
  '2 Chronicles': [17,18,17,22,14,42,22,18,31,19,23,16,22,15,19,14,19,34,11,37,20,12,21,27,28,23,9,27,36,27,21,33,25,33,27,23],
  'Ezra': [11,70,13,24,17,22,28,36,15,44],
  'Nehemiah': [11,20,32,23,19,19,73,18,38,39,36,47,31],
  'Esther': [22,23,15,17,14,14,10,17,32,13,12,6,18,19,16,24],
  'Job': [22,13,26,21,27,30,21,22,35,22,20,25,28,22,35,22,16,21,29,29,34,30,17,25,6,14,23,28,25,31,40,22,33,37,16,33,24,41,30,24,34,17],
  'Psalms': [6,12,8,8,12,10,17,9,20,18,7,8,6,7,5,11,15,50,14,9,13,31,6,10,22,12,14,9,11,12,24,11,22,22,28,12,40,22,13,17,13,11,5,26,17,11,9,14,20,23,19,9,6,7,23,13,11,11,17,12,8,12,11,10,13,20,7,35,36,5,24,20,28,23,10,12,20,72,13,19,16,8,18,12,13,17,7,18,52,17,16,15,5,23,11,13,12,9,9,5,8,28,22,35,45,48,43,13,31,7,10,10,9,8,18,19,2,29,176,7,8,9,4,8,5,6,5,6,8,8,3,18,3,3,21,26,9,8,24,13,10,7,12,15,21,10,20,14,9,6],
  'Proverbs': [33,22,35,27,23,35,27,36,18,32,31,28,25,35,33,33,28,24,29,30,31,29,35,34,28,28,27,28,27,33,31],
  'Ecclesiastes': [18,26,22,16,20,12,29,17,18,20,10,14],
  'Song of Solomon': [17,17,11,16,16,13,13,14],
  'Isaiah': [31,22,26,6,30,13,25,22,21,34,16,6,22,32,9,14,14,7,25,6,17,25,18,23,12,21,13,29,24,33,9,20,24,17,10,22,38,22,8,31,29,25,28,28,25,13,15,22,26,11,23,15,12,17,13,12,21,14,21,22,11,12,19,12,25,24],
  'Jeremiah': [19,37,25,31,31,30,34,22,26,25,23,17,27,22,21,21,27,23,15,18,14,30,40,10,38,24,22,17,32,24,40,44,26,22,19,32,21,28,18,16,18,22,13,30,5,28,7,47,39,46,64,34],
  'Lamentations': [22,22,66,22,22],
  'Ezekiel': [28,10,27,17,17,14,27,18,11,22,25,28,23,23,8,63,24,32,14,49,32,31,49,27,17,21,36,26,21,26,18,32,33,31,15,38,28,23,29,49,26,20,27,31,25,24,23,35],
  'Daniel': [21,49,30,37,31,28,28,27,27,21,45,13],
  'Hosea': [11,23,5,19,15,11,16,14,17,15,12,14,16,9],
  'Joel': [20,32,21],
  'Amos': [15,16,15,13,27,14,17,14,15],
  'Obadiah': [21],
  'Jonah': [17,10,10,11],
  'Micah': [16,13,12,13,15,16,20],
  'Nahum': [15,13,19],
  'Habakkuk': [17,20,19],
  'Zephaniah': [18,15,20],
  'Haggai': [15,23],
  'Zechariah': [21,13,10,14,11,15,14,23,17,12,17,14,9,21],
  'Malachi': [14,17,18,6],
  'Matthew': [25,23,17,25,48,34,29,34,38,42,30,50,58,36,39,28,27,35,30,34,46,46,39,51,46,75,66,20],
  'Mark': [45,28,35,41,43,56,37,38,50,52,33,44,37,72,47,20],
  'Luke': [80,52,38,44,39,49,50,56,62,42,54,59,35,35,32,31,37,43,48,47,38,71,56,53],
  'John': [51,25,36,54,47,71,53,59,41,42,57,50,38,31,27,33,26,40,42,31,25],
  'Acts': [26,47,26,37,42,15,60,40,43,48,30,25,52,28,41,40,34,28,41,38,40,30,35,27,27,32,44,31],
  'Romans': [32,29,31,25,21,23,25,39,33,21,36,21,14,23,33,27],
  '1 Corinthians': [31,16,23,21,13,20,40,13,27,33,34,31,13,40,58,24],
  '2 Corinthians': [24,17,18,18,21,18,16,24,15,18,33,21,14],
  'Galatians': [24,21,29,31,26,18],
  'Ephesians': [23,22,21,32,33,24],
  'Philippians': [30,30,21,23],
  'Colossians': [29,23,25,18],
  '1 Thessalonians': [10,20,13,18,28],
  '2 Thessalonians': [12,17,18],
  '1 Timothy': [20,15,16,16,25,21],
  '2 Timothy': [18,26,17,22],
  'Titus': [16,15,15],
  'Philemon': [25],
  'Hebrews': [14,18,19,16,14,20,28,13,28,39,40,29,25],
  'James': [27,26,18,17,20],
  '1 Peter': [25,25,22,19,14],
  '2 Peter': [21,22,18],
  '1 John': [10,29,24,21,21],
  '2 John': [13],
  '3 John': [14],
  'Jude': [25],
  'Revelation': [20,29,22,11,14,17,17,13,21,11,19,17,18,20,8,21,18,24,21,15,27,21],
};
// Number of verses in a given book+chapter (defaults to 176 if unknown).
export const verseCountFor = (book, chapter) => {
  const arr = BOOK_VERSE_COUNTS[book];
  if (!arr) return 176;
  const n = arr[(parseInt(chapter, 10) || 1) - 1];
  return n || 176;
};
